import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
const API_BASE = process.env.REACT_APP_API_BASE !== undefined
    ? process.env.REACT_APP_API_BASE
    : "http://localhost:8080";
// Venue-local (Atlantic) today as YYYY-MM-DD, so past closures drop off automatically.
function venueTodayStr() {
    return new Date().toLocaleDateString("en-CA", { timeZone: "America/Halifax" });
}
// "2026-07-26" -> "Sunday, July 26, 2026" (parsed as a plain calendar date, no TZ shift).
export function formatClosedDate(dateStr) {
    const [y, m, d] = dateStr.split("-").map(Number);
    if (!y || !m || !d)
        return dateStr;
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}
export async function fetchClosedDates() {
    try {
        const res = await fetch(`${API_BASE}/api/bookings/closed-dates`, {
            credentials: "include",
        });
        if (!res.ok)
            return [];
        const data = await res.json();
        return Array.isArray(data.closedDates) ? data.closedDates : [];
    }
    catch {
        return [];
    }
}
/**
 * Dismissible banner announcing upcoming venue closures.
 * Reads the public `closed_dates` setting; shows nothing when there are no
 * current/future closures. Re-appears if the set of closures changes.
 */
export function ClosureBanner() {
    const [closures, setClosures] = useState([]);
    const [dismissed, setDismissed] = useState(false);
    useEffect(() => {
        let active = true;
        fetchClosedDates().then((all) => {
            if (!active)
                return;
            const today = venueTodayStr();
            const upcoming = all
                .filter((c) => c.date >= today)
                .sort((a, b) => a.date.localeCompare(b.date));
            setClosures(upcoming);
            // Only stay dismissed while the exact set of upcoming closures is unchanged.
            const signature = upcoming.map((c) => c.date).join(",");
            const stored = localStorage.getItem("closureBannerDismissed");
            setDismissed(stored !== null && stored === signature);
        });
        return () => {
            active = false;
        };
    }, []);
    if (dismissed || closures.length === 0)
        return null;
    const handleDismiss = () => {
        const signature = closures.map((c) => c.date).join(",");
        localStorage.setItem("closureBannerDismissed", signature);
        setDismissed(true);
    };
    return (_jsx("div", { className: "bg-amber-500/10 border-b border-amber-500/30", children: _jsxs("div", { className: "container mx-auto px-4 py-3 flex items-start gap-3", children: [_jsx(AlertTriangle, { className: "h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" }), _jsx("div", { className: "flex-1 text-sm text-amber-100", children: closures.map((c) => (_jsxs("p", { className: "leading-snug", children: [_jsxs("span", { className: "font-semibold text-amber-300", children: ["K one Golf is closed on ", formatClosedDate(c.date), "."] }), " ", c.reason ? c.reason : "Online booking is unavailable that day."] }, c.date))) }), _jsx("button", { type: "button", onClick: handleDismiss, "aria-label": "Dismiss closure notice", className: "text-amber-300/70 hover:text-amber-200 flex-shrink-0", children: _jsx(X, { className: "h-4 w-4" }) })] }) }));
}
